import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, GeoJSON, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getDroneMarkerSVG, getEffectiveHeading } from '../utils/icons';
import { intentMarkerColor, intentDisplay, isOffNominal } from '../utils/intentState';
import { drawnRoute } from '../utils/flightPath';
import { API_URL, apiFetch } from '../config/api';
import { MAP_STYLES } from '../config/mapStyles';
import DroneCommandBox from './map/DroneCommandBox';
import { ZoomGroup } from './mobile/MobileKit';
import { onDroneFocus } from '../utils/voiceLiveBus';


delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const FALLBACK_STYLE = {
  label: 'OpenStreetMap',
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap',
  maxZoom: 20,
  maxNativeZoom: 19,
  free: true,
};

function PopupStat({ value, label, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
      <span style={{ fontFamily: 'var(--font-label)', fontSize: '13px', fontWeight: 600, color: color || 'var(--textPrimary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
      <span style={{ fontFamily: 'var(--font-label)', fontSize: '8px', letterSpacing: '0.12em', color: 'var(--textMuted)' }}>{label}</span>
    </div>
  );
}

// Where the copter will climb: where it stands while disarmed, else its recorded home.
function routeHome(drone, telemetry) {
  if (telemetry && !telemetry.armed && Number(telemetry.lat) && Number(telemetry.lon)) return { lat: telemetry.lat, lon: telemetry.lon };
  return drone.homeLocation || null;
}

function Panner({ position }) {
  const map = useMap();
  const prevPositionRef = useRef(null);

  useEffect(() => {
    if (position && position !== prevPositionRef.current) {
      const [lat, lng] = position;
      const prevPos = prevPositionRef.current;

      if (!prevPos || prevPos[0] !== lat || prevPos[1] !== lng) {
        const currentZoom = map.getZoom();
        map.setView(position, currentZoom, { animate: false });
        prevPositionRef.current = position;
      }
    }
  }, [position, map]);

  return null;
}

function DroneNavigator({ targetCoords }) {
  const map = useMap();
  const lastFlownRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(targetCoords)) return;
    const lat = Number(targetCoords[0]);
    const lon = Number(targetCoords[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) return;
    const last = lastFlownRef.current;
    if (last && last[0] === lat && last[1] === lon) return;
    lastFlownRef.current = [lat, lon];
    // A hidden map (3D view active) is 0×0; Leaflet's flyTo animation then
    // divides by zero and throws "Invalid LatLng (NaN, NaN)". Jump instead.
    const size = map.getSize();
    if (!size.x || !size.y) map.setView([lat, lon], 16, { animate: false });
    else map.flyTo([lat, lon], 16, { animate: true, duration: 1.5 });
  }, [targetCoords, map]);
  return null;
}

/** Stitch-style stacked zoom buttons. Phone: the Flutter zoom group above the voice FAB. */
function ZoomControls({ isCompact, locateTarget = null }) {
  const map = useMap();

  if (isCompact) {
    return (
      <div className="m-zoom-dock">
        <ZoomGroup
          onZoomIn={() => map.zoomIn(0.5)}
          onZoomOut={() => map.zoomOut(0.5)}
          onLocate={locateTarget ? () => map.flyTo(locateTarget, Math.max(map.getZoom(), 16)) : null}
        />
      </div>
    );
  }

  const btnStyle = {
    width: 36,
    height: 36,
    background: 'transparent',
    border: 'none',
    color: 'var(--accent)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
    transition: 'background-color 0.2s',
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '24px',
        left: 'var(--map-left-inset, 16px)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 900,
        background: 'rgba(27, 28, 29, 0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 218, 243, 0.3)',
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.45)',
        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <button
        onClick={() => map.zoomIn(0.5)}
        style={{ ...btnStyle, borderBottom: '1px solid rgba(0, 218, 243, 0.12)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0, 218, 243, 0.18)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        aria-label="Zoom in"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>add</span>
      </button>
      <button
        onClick={() => map.zoomOut(0.5)}
        style={btnStyle}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0, 218, 243, 0.18)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        aria-label="Zoom out"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>remove</span>
      </button>
    </div>
  );
}

function MapClickHandler({ missionMode, selectedDrone, onWaypointAdd }) {
  useMapEvents({
    click: (e) => {
      if (missionMode && selectedDrone && onWaypointAdd) {
        const { lat, lng } = e.latlng;
        onWaypointAdd(lat, lng);
      }
    }
  });
  return null;
}

function MapView({
  location,
  dronesTelemetry = {},
  activeMissions = {},
  drones = [],
  missionMode = false,
  selectedDrone = '',
  selectedDroneId = null,
  waypoints = [],
  onWaypointAdd = null,
  onDroneSelect = null,
  isMobileLayout = false,
  currentUserRole = 'operator',
  // Controlled by the MapView orchestrator (Stitch UI)
  currentStyle = 'OpenStreetMap',
  geoJson = null,
  selectedZones = [],
  searchTarget = null,
  locateTarget = null, // phone zoom group's "go to drone"
}) {
  const [panToCoords, setPanToCoords] = useState(null);
  const prevSelectedDroneIdRef = useRef(null);

  // Automatically center map ONLY when a different drone is selected (not on telemetry updates)
  useEffect(() => {
    if (selectedDroneId && selectedDroneId !== prevSelectedDroneIdRef.current) {
      prevSelectedDroneIdRef.current = selectedDroneId;
      const liveTelemetry = dronesTelemetry[selectedDroneId];
      const dbDrone = drones.find(d => d.id === selectedDroneId);
      const tel = liveTelemetry || dbDrone?.telemetry;
      if (tel && tel.lat && tel.lon && tel.lat !== 0 && tel.lon !== 0) {
        setPanToCoords([tel.lat, tel.lon]);
        setTimeout(() => setPanToCoords(null), 2000);
      }
    } else if (!selectedDroneId) {
      prevSelectedDroneIdRef.current = null;
    }
  }, [selectedDroneId, dronesTelemetry, drones]);

  // Selecting a drone pans the map, but only when the selection changes — asking
  // to focus the drone already selected has to recentre anyway.
  useEffect(() => onDroneFocus((droneId) => {
    const tel = dronesTelemetry[droneId] || drones.find((d) => d.id === droneId)?.telemetry;
    if (!tel?.lat || !tel?.lon || (tel.lat === 0 && tel.lon === 0)) return;
    setPanToCoords([tel.lat, tel.lon]);
    setTimeout(() => setPanToCoords(null), 2000);
  }), [dronesTelemetry, drones]);

  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [droneMissions, setDroneMissions] = useState({}); // droneId -> [{id, name, waypoints}]
  const prevDronePositions = useRef({}); // droneId -> { lat, lon }

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const visibleDrones = useMemo(() => {
    if (missionMode) {
      return drones.map((d) => ({ ...d, readonly: false }));
    }

    // Other operators' aircraft in the air, under their daily handle (the
    // server sends nothing else about them): grey, not selectable.
    const own = new Set(drones.map((d) => d.id));
    const shared = Object.entries(dronesTelemetry)
      .filter(([id, tel]) => tel?.readonly && !own.has(id))
      .map(([id, tel]) => ({ id, name: tel.droneName || id, vehicleModel: tel.vehicleModel, readonly: true }));

    return drones
      .filter((drone) => {
        const tel = dronesTelemetry[drone.id] || drone.telemetry;
        const mode = String(tel?.mode || '').trim().toUpperCase();
        return !!tel?.connected && !tel?.stale && !!mode && mode !== 'N/A' && mode !== 'DISCONNECTED';
      })
      .map((drone) => ({ ...drone, readonly: false }))
      .concat(shared);
  }, [drones, dronesTelemetry, missionMode]);

  // Extract fleet markers into a tightly scoped memo component so Leaflet MapContainer doesn't diff its entire tree at 2Hz
  const ActiveFleetMarkers = useMemo(() => {
    return (
      <>
        {!missionMode && visibleDrones.map((drone) => {
          const currentTelemetry = dronesTelemetry[drone.id] || drone.telemetry;
          const isConnected = !!currentTelemetry?.connected;
          const isReadonly = !!drone.readonly;
          const parsedRelativeAlt = parseFloat(currentTelemetry?.relative_alt);
          const parsedFallbackAlt = parseFloat(currentTelemetry?.alt);
          const displayAlt = Number.isFinite(parsedRelativeAlt)
            ? parsedRelativeAlt
            : (Number.isFinite(parsedFallbackAlt) ? parsedFallbackAlt : 0);

          // Only show connected drones with valid coordinates
          const droneLat = currentTelemetry?.lat || 0;
          const droneLon = currentTelemetry?.lon || 0;
          if (!isConnected || !droneLat || !droneLon || (droneLat === 0 && droneLon === 0)) return null;

          const lastPos = prevDronePositions.current[drone.id] || null;
          const droneHeading = getEffectiveHeading(currentTelemetry, lastPos);
          if (droneLat !== 0 || droneLon !== 0) {
            prevDronePositions.current[drone.id] = { lat: droneLat, lon: droneLon };
          }

          const iconSize = 48;
          // An off-nominal airspace claim overrides the usual owned/shared
          // colour, for other operators' aircraft too: a neighbour leaving its
          // corridor lights up here.
          const intentColor = intentMarkerColor(currentTelemetry?.intentState);
          const iconColor = intentColor || (isReadonly ? '#94a3b8' : '#00daf3');

          const droneIcon = L.divIcon({
            className: 'drone-marker',
            html: getDroneMarkerSVG({
              size: iconSize,
              color: iconColor,
              heading: droneHeading,
              isSelected: false,
              isConnected: isConnected,
              label: drone.name?.charAt(0).toUpperCase() || 'D'
            }),
            iconSize: [iconSize, iconSize],
            iconAnchor: [iconSize / 2, iconSize / 2]
          });

          const markerHandlers = !isReadonly && onDroneSelect
            ? { click: () => onDroneSelect(drone) }
            : undefined;

          // Read-only markers have no popup, so the native tooltip is the only
          // way a foreign drone's claim state reaches the operator at all.
          const intentInfo = intentDisplay(currentTelemetry?.intentState);
          const markerTitle = intentInfo && isOffNominal(currentTelemetry?.intentState)
            ? `${drone.name || drone.id} — ${intentInfo.label}`
            : undefined;

          return (
            <Marker
              key={drone.id}
              position={[droneLat, droneLon]}
              icon={droneIcon}
              interactive={!isReadonly}
              title={markerTitle}
              eventHandlers={markerHandlers}
            >
              {!isReadonly && (
              // Phone: autopan keeps the popup clear of the glass top bar and tab bar.
              <Popup {...(isMobileLayout ? { autoPanPaddingTopLeft: [12, 80], autoPanPaddingBottomRight: [12, 96] } : {})}>
                <div style={{ width: '288px', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                  {/* Identity line: name, type and link state read as one row so
                      the numbers below get the eye instead of six bold labels. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, background: isConnected ? '#10b981' : '#6b7280', animation: isConnected ? 'pulse 1.5s infinite' : 'none' }}></span>
                    <strong style={{ color: iconColor, fontSize: '14px' }}>{drone.name}</strong>
                    <span style={{ fontFamily: 'var(--font-label)', fontSize: '10px', letterSpacing: '0.08em', color: 'var(--textMuted)' }}>
                      {drone.type || 'N/A'} · {isConnected ? 'LINKED' : 'NO LINK'}
                    </span>
                  </div>

                  {currentTelemetry && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '10px' }}>
                        <PopupStat
                          value={(Number.isFinite(parsedRelativeAlt) || Number.isFinite(parsedFallbackAlt)) ? `${displayAlt.toFixed(1)}m` : '—'}
                          label="ALT AGL"
                        />
                        <PopupStat
                          value={currentTelemetry.battery_remaining !== undefined ? `${currentTelemetry.battery_remaining.toFixed(0)}%` : '—'}
                          label="BATTERY"
                          color={currentTelemetry.battery_remaining !== undefined
                            ? (currentTelemetry.battery_remaining > 40 ? 'var(--success)' : currentTelemetry.battery_remaining > 20 ? 'var(--warning)' : 'var(--danger)')
                            : undefined}
                        />
                        <PopupStat value={currentTelemetry.mode || '—'} label="MODE" />
                      </div>

                      <div style={{ fontFamily: 'var(--font-label)', fontSize: '11px', color: 'var(--textSecondary)', marginTop: '8px' }}>
                        {droneLat.toFixed(6)}, {droneLon.toFixed(6)}
                      </div>

                      {intentInfo && (
                        <div style={{ fontFamily: 'var(--font-label)', fontSize: '10px', letterSpacing: '0.08em', marginTop: '4px', color: intentInfo.color || 'var(--textMuted)' }}>
                          {intentInfo.label}
                        </div>
                      )}
                    </>
                  )}

                  <DroneCommandBox droneId={drone.id} droneName={drone.name} telemetry={currentTelemetry || {}} />
                </div>
              </Popup>
              )}
            </Marker>
          );
        })}

        {/* Mission Routes for all active drones - dotted lines */}
        {!missionMode && (() => {
          const ROUTE_COLORS = ['#00daf3', '#f59e0b', '#10b981', '#a855f7', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6'];
          return drones.map((drone, droneIdx) => {
            const currentTelemetry = dronesTelemetry[drone.id] || drone.telemetry;
            if (!currentTelemetry?.connected) return null;

            const missions = droneMissions[drone.id] || [];
            return missions.map(mission => {
              const wps = drawnRoute(mission.waypoints, routeHome(drone, currentTelemetry));
              if (wps.length < 2) return null;
              const color = ROUTE_COLORS[droneIdx % ROUTE_COLORS.length];
              return (
                <Polyline
                  key={`route-${drone.id}-${mission.id}`}
                  positions={wps}
                  pathOptions={{ color: color, weight: 2.5, opacity: 0.7, dashArray: '8, 8' }}
                />
              );
            });
          });
        })()}
      </>
    );
  // Using useMemo to only diff these markers when drones/telemetry references change, ignoring unrelated MapView state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drones, visibleDrones, dronesTelemetry, missionMode, onDroneSelect, droneMissions, isMobileLayout]);

  const isCompact = isMobileLayout || viewportWidth <= 768;

  const lat = location?.lat ?? 0;
  const lon = location?.lon ?? 0;
  const defaultCenter = [21.1458, 79.0882]; // Also defined in config/constants.js
  const activeSearchTarget = searchTarget;
  const center = activeSearchTarget ? activeSearchTarget : ((lat !== 0 && lon !== 0) ? [lat, lon] : defaultCenter);

  const styleConfig = MAP_STYLES[currentStyle] || MAP_STYLES.OpenStreetMap || FALLBACK_STYLE;

  const [missionRefreshTick, setMissionRefreshTick] = useState(0);

  // Refresh route previews the moment a mission is created/saved/deleted
  useEffect(() => {
    const onMissionChanged = () => setMissionRefreshTick(t => t + 1);
    window.addEventListener('utms:mission-saved', onMissionChanged);
    return () => window.removeEventListener('utms:mission-saved', onMissionChanged);
  }, []);

  // Load active mission routes only (avoids per-drone polling storm)
  useEffect(() => {
    if (!drones.length) {
      setDroneMissions({});
      return;
    }

    let isMounted = true;

    const loadActiveMissionRoutes = async () => {
      const targets = drones
        .map((drone) => {
          const missionId = activeMissions?.[drone.id]?.missionId;
          return missionId ? { droneId: drone.id, missionId } : null;
        })
        .filter(Boolean);

      if (targets.length === 0) {
        if (isMounted) setDroneMissions({});
        return;
      }

      const entries = await Promise.all((targets || []).filter(Boolean).map(async (t) => {
        if (!t || !t.droneId) return [null, []];
        const { droneId, missionId } = t;
        try {
          const res = await apiFetch(`${API_URL}/drones/${droneId}/missions/${missionId}`);
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.mission) {
              return [droneId, [data.mission]];
            }
          }
        } catch { /* silent */ }
        return [droneId, []];
      }));

      if (!isMounted) return;
      setDroneMissions(Object.fromEntries(entries));
    };

    loadActiveMissionRoutes();
    return () => { isMounted = false; };
  }, [drones, activeMissions, missionRefreshTick]);

  function getFeatureStyle(feature) {
    const props = feature.properties || {};
    const color = props.stroke || props.color || '#fb0909';
    return {
      color,
      weight: props['stroke-width'] || 3,
      opacity: 0.5,
      fillColor: props.fill || color,
      fillOpacity: 0.1,
    };
  }

  function onEachFeature(feature, layer) {
    const props = feature.properties || {};
    const name = props.zone || props.name || 'Zone';
    layer.bindPopup(name);
  }

  // Filter GeoJSON features based on selected zones
  const filteredGeoJson = React.useMemo(() => {
    if (!geoJson || !Array.isArray(geoJson.features)) {
      return null;
    }

    const filteredFeatures = geoJson.features.filter(feature => {
      const zoneName = feature.properties?.zone || feature.properties?.name;
      return selectedZones.includes(zoneName);
    });

    return {
      ...geoJson,
      features: filteredFeatures
    };
  }, [geoJson, selectedZones]);

  // Generate stable key for GeoJSON to prevent unnecessary re-renders
  const geoJsonKey = React.useMemo(() => {
    if (!filteredGeoJson || !Array.isArray(filteredGeoJson.features)) {
      return 'no-zones';
    }
    return `zones-${filteredGeoJson.features.length}-${[...selectedZones].sort().join('-')}`;
  }, [filteredGeoJson, selectedZones]);

  return (
    <div style={{ flex: 1, position: 'relative', height: '100%' }}>
      {/* Map Container */}
      <div style={{ position: 'relative', height: '100%', width: '100%', margin: 0, padding: 0, overflow: 'hidden' }}>

        <MapContainer
          center={center}
          zoom={10}
          style={{ height: '100%', width: '100%', margin: 0, padding: 0 }}
          zoomControl={false}
          attributionControl={false}
          maxZoom={22}
          minZoom={3}
          zoomSnap={0.25}
          zoomDelta={0.5}
          wheelPxPerZoomLevel={100}
        >
          <TileLayer
            key={currentStyle}
            attribution={styleConfig.attribution}
            url={styleConfig.url}
            tileSize={styleConfig.tileSize || 256}
            zoomOffset={styleConfig.zoomOffset || 0}
            maxZoom={styleConfig.maxZoom || 20}
            maxNativeZoom={styleConfig.maxNativeZoom || 19}
          />

          {/* Custom Zoom Controls */}
          <ZoomControls isCompact={isCompact} locateTarget={locateTarget} />

          {/* Map click handler for mission mode */}
          <MapClickHandler
            missionMode={missionMode}
            selectedDrone={selectedDrone}
            onWaypointAdd={onWaypointAdd}
          />

          {/* Render zones from map.geojson */}
          {filteredGeoJson && Array.isArray(filteredGeoJson.features) && filteredGeoJson.features.length > 0 && (
            <GeoJSON
              key={geoJsonKey}
              data={filteredGeoJson}
              style={getFeatureStyle}
              onEachFeature={onEachFeature}
            />
          )}

          {/* Waypoint markers */}
          {missionMode && waypoints && waypoints.map((waypoint) => (
            <Marker
              key={waypoint.id}
              position={[waypoint.lat, waypoint.lon]}
              icon={L.divIcon({
                className: 'waypoint-marker',
                html: `<div style="
                background: linear-gradient(135deg, #00daf3 0%, #007a8a 100%);
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: 3px solid #fff;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-weight: 600;
                font-size: 14px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              ">${waypoint.order}</div>`,
                iconSize: [32, 32],
                iconAnchor: [16, 16]
              })}
            >
              <Popup>
                <div style={{ textAlign: 'center' }}>
                  <strong>Waypoint {waypoint.order}</strong><br />
                  {waypoint.lat.toFixed(6)}, {waypoint.lon.toFixed(6)}
                </div>
              </Popup>
            </Marker>
          ))}

          {activeSearchTarget && <Panner position={activeSearchTarget} />}
          {panToCoords && <DroneNavigator targetCoords={panToCoords} />}

          {/* Render the memoized fleet markers & routes */}
          {ActiveFleetMarkers}
        </MapContainer>
      </div>
    </div>
  );
}

export default MapView;
